import { useHttpClient } from "../http-client";
import { useSupervisorMode } from "../../components/supervisor-mode-provider";
import { useQuery } from "@tanstack/react-query";
import { ApiResponsesList } from "../types/api-response";

export interface UseGetResponsesApiFilters {
  guesses?: boolean;
  skipped?: boolean;
  processed?: boolean;
  retags?: boolean;
}

const limit = 100;

const setParamOrSkip = (value?: boolean) => (value !== undefined ? value : "");

export const useGetResponsesApi = (
  params: {
    organizationId: string;
    themeStructureId: string;
    isFirstLoad: boolean;
  } & UseGetResponsesApiFilters
) => {
  const httpClient = useHttpClient();
  const isSupervisor = useSupervisorMode();

  const getResponses = async ({
    signal,
  }: {
    signal: AbortSignal;
  }): Promise<ApiResponsesList> => {
    const response = await httpClient.get(`/classifier/responses`, {
      params: {
        organization_id: params.organizationId,
        theme_structure_id: params.themeStructureId,
        supervisor: isSupervisor,
        guesses: setParamOrSkip(params.guesses),
        skipped: setParamOrSkip(params.skipped),
        processed: setParamOrSkip(params.processed),
        retags: setParamOrSkip(params.retags),
        limit,
        first_load: params.isFirstLoad,
      },
      signal,
    });
    return {
      responses: response.data.data,
      total: response.data.meta.total,
    };
  };

  return useQuery({
    // `isFirstLoad` is a one-shot request modifier, not part of the data
    // identity. Including it in the queryKey caused the hook to spin up a
    // second query as soon as the ref flipped to false on mount, abandoning
    // the first (already locked) batch and later re-serving it on
    // buffer-exhaustion refetches.
    queryKey: [
      "responses",
      params.organizationId,
      params.themeStructureId,
      isSupervisor,
      params.guesses,
      params.skipped,
      params.processed,
      params.retags,
    ],
    queryFn: getResponses,
    initialData: {
      responses: [],
      total: 0,
    },
  });
};
